/**
 * HiddenLine (rules v2) card back: the face-down ticket line. A grape card with a diamond lattice,
 * a cream inner frame and a "?" on a cream medallion; it reads by shape and contrast alone, so it
 * holds up in the colour-blind mode (items swap to their patterned art there, the card does not
 * need to). Code-drawn like the lock art: the texture is baked once per game into a canvas.
 */
import type * as Phaser from 'phaser';
import { C, FONT, css } from '../game/theme.js';

export const CARD_BACK = 'fx_card_back';
const S = 128;

function rounded(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Bake the card-back texture (idempotent). */
export function bakeCardBack(scene: Phaser.Scene): void {
  if (scene.textures.exists(CARD_BACK)) return;
  const c = document.createElement('canvas');
  c.width = S;
  c.height = S;
  const ctx = c.getContext('2d')!;
  const [x, y, w, h, r] = [12, 4, S - 24, S - 14, 18];
  rounded(ctx, x, y + 6, w, h, r);
  ctx.fillStyle = 'rgba(74,44,42,0.3)';
  ctx.fill();
  rounded(ctx, x, y, w, h, r);
  ctx.fillStyle = css(C.grape);
  ctx.fill();
  // the lattice: two sets of diagonals, clipped to the card
  ctx.save();
  rounded(ctx, x, y, w, h, r);
  ctx.clip();
  ctx.strokeStyle = 'rgba(255,248,236,0.22)';
  ctx.lineWidth = 5;
  for (let k = -S; k < 2 * S; k += 20) {
    ctx.beginPath();
    ctx.moveTo(k, 0);
    ctx.lineTo(k + S, S);
    ctx.moveTo(k + S, 0);
    ctx.lineTo(k, S);
    ctx.stroke();
  }
  ctx.restore();
  ctx.strokeStyle = css(C.cream);
  ctx.lineWidth = 4;
  rounded(ctx, x + 9, y + 9, w - 18, h - 18, r - 7);
  ctx.stroke();
  ctx.strokeStyle = css(C.ink);
  ctx.lineWidth = 6;
  rounded(ctx, x, y, w, h, r);
  ctx.stroke();
  // the "?" medallion
  const cx = S / 2;
  const cy = y + h / 2;
  ctx.beginPath();
  ctx.arc(cx, cy, 29, 0, Math.PI * 2);
  ctx.fillStyle = css(C.cream);
  ctx.fill();
  ctx.lineWidth = 5;
  ctx.strokeStyle = css(C.ink);
  ctx.stroke();
  ctx.font = `700 46px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = css(C.ink);
  ctx.fillText('?', cx, cy + 2);
  scene.textures.addCanvas(CARD_BACK, c);
}
